import { PlusOutlined } from "@ant-design/icons";
import {
  Button,
  Col,
  Collapse,
  Dropdown,
  Flex,
  Form,
  Input,
  MenuProps,
  message,
  Modal,
  ModalProps,
  Row,
  Select,
  SelectProps,
  Space,
  Tooltip,
  Typography,
} from "antd";
import { ApiService } from "api/api.service";
import { BoardResource, CreateBoardDto } from "dtos/kanban.dto";
import { memo, useMemo } from "react";
import {
  TbBuilding,
  TbFilter,
  TbLayout,
  TbPackage,
  TbPlus,
  TbTrash,
  TbX,
  TbChevronDown,
  TbClipboardText,
  TbLayoutBoard,
  TbLayoutKanban,
  TbRocket,
} from "react-icons/tb";
import { resourceConfig } from "./BoardCard";
import { LuGlobe } from "react-icons/lu";
import { FormInstance, useWatch } from "antd/es/form/Form";
import { ColumnFormValue, ConfigTemplate, ConfigTemplates, GroupFormValue } from "./ConfigTemplates";
import { ColorPicker, getPaletteEntry, IconPicker, pickGroupColor, pickRandomIcon } from "./MetaItems";
// import { LoanStatus } from "dtos/app-creditor-map.dto";
import { toCapitalCase } from "utils/functions";
// import { ACTION_TYPES, LOAN_STATUS_META } from "components/Application/constants";
import { ActionPicker } from "./ActionPicker";

interface CreatePipelineModalProps extends ModalProps {
  initialOrgCode?: string;
}

const BOARD_RESOURCES: SelectProps["options"] = [
  BoardResource.APPLICATION,
  BoardResource.CREDITOR,
  BoardResource.LOAN,
  BoardResource.LEAD,
].map((resource) => {
  const { Icon, label } = resourceConfig[resource];
  return {
    label: (
      <Space>
        <Icon />
        <Typography.Text>{label}</Typography.Text>
      </Space>
    ),
    value: resource,
  };
});

const globalScopeOption = {
  label: (
    <Space>
      <LuGlobe />
      <Typography.Text>Global</Typography.Text>
    </Space>
  ),
  value: null,
};

type FormValues = {
  name: string;
  resource: BoardResource;
  orgCode: string;
  scope?: string;
  groups: GroupFormValue[];
  ungroupedColumns: ColumnFormValue[];
};

const defaultColumn = (withIcon = true): ColumnFormValue =>
  withIcon ? { name: "", description: "", meta: { icon: pickRandomIcon() } } : { name: "", description: "" };

const defaultGroup = (color: string): GroupFormValue => ({
  name: "",
  columns: [defaultColumn()],
  meta: { color },
});

interface ColumnRowProps {
  fieldIndex: number;
  onRemove: () => void;
  canRemove: boolean;
  accentColor?: string;
  resource: BoardResource;
  orgCode?: string;
}

const ColumnRow = memo(function ColumnRow({
  fieldIndex,
  onRemove,
  canRemove,
  accentColor,
  resource,
  orgCode,
}: ColumnRowProps) {
  return (
    <Row
      gutter={10}
      align="top"
      wrap={false}
      style={{
        marginBottom: 8,
        padding: "8px 10px",
        background: "#fff",
        border: "1px solid #f0f0f0",
        borderRadius: 8,
      }}
    >
      <Col style={{ flexShrink: 0, paddingTop: 2 }}>
        <Form.Item name={[fieldIndex, "meta", "icon"]} noStyle>
          <IconPicker accentColor={accentColor} />
        </Form.Item>
      </Col>
      <Col flex="auto" style={{ minWidth: 0 }}>
        <Form.Item name={[fieldIndex, "name"]} noStyle rules={[{ required: true, message: "Column name required" }]}>
          <Input
            placeholder="Column name"
            size="small"
            variant="borderless"
            style={{
              padding: "2px 0",
              fontWeight: 500,
              fontSize: 13,
            }}
          />
        </Form.Item>
        <Form.Item name={[fieldIndex, "description"]} noStyle>
          <Input
            placeholder="Description (optional)"
            size="small"
            variant="borderless"
            style={{
              padding: "2px 0",
              fontSize: 12,
              color: "#6b7280",
            }}
          />
        </Form.Item>
      </Col>
      <Col style={{ flexShrink: 0 }}>
        <Form.Item name={[fieldIndex, "action"]} noStyle>
          <ActionPicker resource={resource} orgCode={orgCode} />
        </Form.Item>
        <Tooltip title="Remove column">
          <Button
            type="text"
            size="small"
            danger
            disabled={!canRemove}
            icon={<TbX style={{ fontSize: 14 }} />}
            onClick={onRemove}
          />
        </Tooltip>
      </Col>
    </Row>
  );
});

const ColumnList = memo(function ColumnList({
  listName,
  accentColor,
  resource,
  newColumnHasIcon = true,
  orgCode,
}: {
  listName: string | (string | number)[];
  accentColor?: string;
  newColumnHasIcon?: boolean;
  resource: BoardResource;
  orgCode?: string;
}) {
  return (
    <Form.List name={listName}>
      {(fields, { add, remove }) => (
        <>
          {fields.map((field) => (
            <ColumnRow
              key={field.key}
              fieldIndex={field.name}
              onRemove={() => remove(field.name)}
              canRemove={fields.length > 1}
              accentColor={accentColor}
              resource={resource}
              orgCode={orgCode}
            />
          ))}
          <Button
            type="text"
            size="small"
            icon={<TbPlus size={13} />}
            onClick={() => add(defaultColumn(newColumnHasIcon))}
            style={{
              color: accentColor ?? "#6d28d9",
              width: "100%",
              marginTop: 4,
              fontWeight: 500,
              justifyContent: "center",
            }}
          >
            Add Column
          </Button>
        </>
      )}
    </Form.List>
  );
});

const GroupPanel = memo(function GroupPanel({
  groupIndex,
  accentColor,
  resource,
  orgCode,
}: {
  groupIndex: number;
  accentColor?: string;
  resource: BoardResource;
  orgCode?: string;
}) {
  return (
    <ColumnList resource={resource} orgCode={orgCode} listName={[groupIndex, "columns"]} accentColor={accentColor} />
  );
});

const chevronIcon = ({ isActive }: { isActive?: boolean }) => (
  <TbChevronDown
    size={16}
    style={{
      transform: isActive ? "rotate(180deg)" : "none",
      transition: "transform 0.2s",
      color: "#6b7280",
    }}
  />
);

export const CreatePipelineModal = ({ initialOrgCode, ...props }: CreatePipelineModalProps) => {
  const [form] = Form.useForm<FormValues>();
  const selectedResource = useWatch(["resource"], form);
  const watchedGroups = useWatch(["groups"], form) ?? [];
  const watchedOrgCode = useWatch(["orgCode"], form) as string | undefined;
  const title = props.title ?? "Create New Pipeline";
  const okText = props.okText ?? "Create Pipeline";

  const createBoard = ApiService.kanban.useCreateBoard();

  const orgs = ApiService.organisation.useGetAllOrganisations();

  const makeOrgLabel = (name: string, image?: string) => (
    <Space size={6}>
      {image ? (
        <img height={20} src={image} style={{ borderRadius: 3 }} />
      ) : (
        <TbBuilding size={14} style={{ color: "#6d28d9", flexShrink: 0 }} />
      )}
      <Typography.Text>{name}</Typography.Text>
    </Space>
  );

  const orgOptions = useMemo(
    () =>
      orgs.data?.length
        ? orgs.data.map((o: any) => {
            const name = o.name ?? o.code;
            return { label: makeOrgLabel(name, o.meta?.favicon || o.meta?.logo), value: o.code, title: name };
          })
        : initialOrgCode
          ? [{ label: makeOrgLabel(initialOrgCode), value: initialOrgCode, title: initialOrgCode }]
          : [],
    [orgs.data, initialOrgCode],
  );

  const scopeOptions: SelectProps["options"] = useMemo(() => {
    if (!selectedResource) return [globalScopeOption];
    const scopes = resourceConfig[selectedResource].possibleConnections ?? [];
    return [globalScopeOption, ...BOARD_RESOURCES.filter((rs) => rs.value && scopes.includes(rs.value as string))];
  }, [selectedResource]);

  const handleOk = () => form.submit();

  return (
    <Modal
      {...props}
      title={title}
      okText={okText}
      onOk={handleOk}
      onCancel={(e) => {
        props.onCancel?.(e);
        form.resetFields();
      }}
      width={700}
      styles={{ body: { maxHeight: "60vh", overflowY: "auto", paddingRight: 8 } }}
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{ orgCode: initialOrgCode, groups: [], ungroupedColumns: [defaultColumn(false)] }}
        onFinish={(values) => {
          if (!values.orgCode) return message.warning("Organisation not selected");
          const { orgCode: selectedOrg, ...rest } = values;
          const groups: CreateBoardDto["groups"] = rest.groups.map((grp, i) => ({
            name: grp.name,
            position: i,
            ...(grp.meta ? { meta: grp.meta } : {}),
          }));

          const buildColumnMeta = (col: ColumnFormValue) => {
            const meta: Record<string, any> = {};
            if (col.meta?.icon) meta.icon = col.meta.icon;
            if (col.description?.trim()) meta.description = col.description.trim();
            return Object.keys(meta).length ? meta : undefined;
          };

          const groupedCols =
            values.groups?.flatMap((g) =>
              (g.columns ?? []).map((col) => ({
                name: col.name,
                group: g.name,
                action: col.action,
                meta: buildColumnMeta(col),
              })),
            ) ?? [];
          const ungrouped = (values.ungroupedColumns ?? []).map((col) => ({
            name: col.name,
            group: undefined,
            meta: buildColumnMeta(col),
          }));
          const columns: CreateBoardDto["columns"] = [...groupedCols, ...ungrouped]
            .map((col, i) => ({
              ...col,
              position: i,
              disabled: false,
            }))
            .filter((col) => col.name.length);

          if (!columns.length) {
            return message.warning("Can not create board with 0 columns");
          }

          if (rest.scope === null) rest.scope = undefined;
          createBoard.mutate(
            { orgCode: selectedOrg, ...rest, groups, columns },
            {
              onSuccess(data) {
                message.success(`Board created with ${data.result.columns.length} columns`);
                form.resetFields();
                props.onOk?.({} as React.MouseEvent<HTMLButtonElement>);
              },
            },
          );
        }}
      >
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="name"
              label={
                <Space size={4}>
                  <TbLayout size={14} />
                  Pipeline Name
                </Space>
              }
              rules={[{ required: true, message: "Please enter a pipeline name" }]}
            >
              <Input placeholder="e.g. All Applications" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="orgCode"
              label={
                <Space size={4}>
                  <TbBuilding size={14} />
                  Organisation
                </Space>
              }
              rules={[{ required: true, message: "Please select an organisation" }]}
            >
              <Select
                showSearch
                placeholder="Select organisation"
                options={orgOptions}
                filterOption={(input, option) => option?.title?.toLowerCase().includes(input.toLowerCase())}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="resource"
              label=<Space size={4}>
                <TbPackage size={14} />
                Resource
              </Space>
              rules={[{ required: true, message: "Please select a resource" }]}
            >
              <Select
                showSearch
                placeholder="Select resource type"
                options={BOARD_RESOURCES.filter(
                  (rs) =>
                    typeof rs.value === "string" &&
                    ![BoardResource.CREDITOR, BoardResource.LEAD].includes(rs.value as BoardResource),
                )}
                filterOption={(input, option) => (option?.label as string)?.toLowerCase().includes(input.toLowerCase())}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="scope"
              initialValue={null}
              label={
                <Space size={4}>
                  <TbFilter size={14} />
                  Scope
                </Space>
              }
            >
              <Select
                disabled={!selectedResource}
                showSearch
                placeholder="Select scope"
                options={scopeOptions}
                filterOption={(input, option) => (option?.label as string)?.toLowerCase().includes(input.toLowerCase())}
              />
            </Form.Item>
          </Col>
        </Row>

        {/* <Divider orientation="left" style={{ fontSize: 13, color: "#6b7280", margin: "4px 0 12px" }}></Divider> */}
        <Space size={8} className="justify-between w-full mt-1 mb-3">
          <Space size={4}>
            <TbLayoutBoard size={14} />
            Columns & Groups
          </Space>
          <LoadConfig form={form} resource={selectedResource} />
        </Space>

        <Collapse
          bordered={false}
          expandIconPosition="end"
          expandIcon={chevronIcon}
          style={{ marginBottom: 6, background: "transparent" }}
          items={[
            {
              key: "ungrouped",
              collapsible: "icon",
              style: {
                border: "1px solid #f0f0f0",
                borderRadius: 8,
                marginBottom: 6,
                overflow: "hidden",
                background: "#fafafa",
              },
              styles: {
                header: {
                  padding: "10px 12px",
                  minHeight: 0,
                  background: "#fafafa",
                },
              },
              label: (
                <Typography.Text style={{ fontSize: 12, color: "#6b7280", fontWeight: 500 }}>
                  Ungrouped columns
                </Typography.Text>
              ),
              children: (
                <div style={{ padding: "4px 12px 12px" }}>
                  <ColumnList
                    resource={selectedResource}
                    listName="ungroupedColumns"
                    accentColor="#9ca3af"
                    newColumnHasIcon={false}
                    orgCode={watchedOrgCode}
                  />
                </div>
              ),
            },
          ]}
        />

        {/* Groups */}
        <Form.List name="groups">
          {(groupFields, { add: addGroup, remove: removeGroup }) => (
            <>
              {groupFields.length > 0 && (
                <Collapse
                  bordered={false}
                  accordion
                  defaultActiveKey={groupFields.length ? [groupFields[groupFields.length - 1].key] : []}
                  expandIconPosition="end"
                  expandIcon={chevronIcon}
                  style={{ marginBottom: 8, background: "transparent" }}
                  items={groupFields.map((groupField, idx) => {
                    const groupColor = watchedGroups[groupField.name]?.meta?.color;
                    const palette = getPaletteEntry(groupColor, idx);
                    return {
                      key: groupField.key,
                      collapsible: "icon",
                      style: {
                        border: `1px solid ${palette.border}`,
                        borderLeft: `3px solid ${palette.solid}`,
                        borderRadius: 8,
                        marginBottom: 6,
                        overflow: "hidden",
                        background: "#fff",
                        boxShadow: "0 1px 2px rgba(0,0,0,0.02)",
                      },
                      headerStyle: {
                        padding: "8px 12px",
                        minHeight: 0,
                        background: palette.soft,
                      },
                      label: (
                        <Row align="middle" gutter={10} wrap={false}>
                          <Col style={{ flexShrink: 0, display: "flex", alignItems: "center" }}>
                            <Form.Item name={[groupField.name, "meta", "color"]} noStyle>
                              <ColorPicker />
                            </Form.Item>
                          </Col>
                          <Col flex="auto">
                            <Form.Item
                              name={[groupField.name, "name"]}
                              noStyle
                              rules={[{ required: true, message: "Group name required" }]}
                            >
                              <Input
                                size="small"
                                placeholder="Group name"
                                variant="borderless"
                                style={{
                                  fontWeight: 600,
                                  background: "transparent",
                                  padding: 0,
                                  color: "#111827",
                                }}
                              />
                            </Form.Item>
                          </Col>
                          <Col style={{ flexShrink: 0 }}>
                            <Tooltip title="Remove group">
                              <Button
                                type="text"
                                size="small"
                                danger
                                icon={<TbTrash style={{ fontSize: 14 }} />}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  removeGroup(groupField.name);
                                }}
                              />
                            </Tooltip>
                          </Col>
                        </Row>
                      ),
                      children: (
                        <div style={{ padding: "10px 14px 12px" }}>
                          <GroupPanel
                            resource={selectedResource}
                            groupIndex={groupField.name}
                            accentColor={palette.solid}
                            orgCode={watchedOrgCode}
                          />
                        </div>
                      ),
                    };
                  })}
                />
              )}

              <Button
                type="dashed"
                onClick={() => addGroup(defaultGroup(pickGroupColor(watchedGroups)))}
                icon={<PlusOutlined />}
                style={{
                  borderColor: "#c4b5fd",
                  color: "#6d28d9",
                  width: "100%",
                  background: "#faf5ff",
                  fontWeight: 500,
                }}
              >
                Add Group
              </Button>
            </>
          )}
        </Form.List>
      </Form>
    </Modal>
  );
};

const LoadConfig = ({ form, resource }: { form: FormInstance; resource: BoardResource | undefined }) => {
  const eligibleTemplates = useMemo(() => {
    if (!resource) return [];
    return ConfigTemplates.filter((tmpl) => tmpl.for.includes(resource));
  }, [resource, ConfigTemplates]);

  const applyConfig = (config: ConfigTemplate["config"], label: string) => {
    const groups = config.groups.map((g) => ({
      ...g,
      columns: g.columns.length ? g.columns : [defaultColumn()],
    }));
    const ungroupedColumns = config.ungroupedColumns.length ? config.ungroupedColumns : [defaultColumn(false)];
    form.setFieldsValue({ groups, ungroupedColumns });
    message.success(`Loaded: ${label}`);
  };

  const handlePasteFromClipboard = async () => {
    let raw: string;
    try {
      raw = await navigator.clipboard.readText();
    } catch {
      message.error("Clipboard access denied");
      return;
    }

    let parsed: any;
    try {
      parsed = JSON.parse(raw);
    } catch {
      message.error("Clipboard does not contain valid JSON");
      return;
    }

    if (!Array.isArray(parsed?.groups) || !Array.isArray(parsed?.ungroupedColumns)) {
      message.error("Payload is missing groups or ungroupedColumns");
      return;
    }

    const toColumnFormValue = (col: any): ColumnFormValue => ({
      name: col.name ?? "",
      description: col.meta?.description ?? "",
      action: col.action,
      meta: { icon: col.meta?.icon },
    });

    applyConfig(
      {
        groups: parsed.groups.map((g: any) => ({
          name: g.name ?? "",
          meta: { color: g.meta?.color ?? pickGroupColor([]) },
          columns: Array.isArray(g.columns) ? g.columns.map(toColumnFormValue) : [],
        })),
        ungroupedColumns: parsed.ungroupedColumns.map(toColumnFormValue),
      },
      `${parsed.groups.length} group${parsed.groups.length === 1 ? "" : "s"} from clipboard`,
    );
  };

  const loadConfigMenuItems: MenuProps["items"] = [
    {
      key: "paste",
      label: "Paste from clipboard",
      icon: <TbClipboardText size={14} />,
    },
    { type: "divider" },
    ...eligibleTemplates.map((t) => ({
      key: t.key,
      label: t.label,
      icon: <TbLayoutKanban size={14} />,
    })),
  ];

  const handleLoadConfigMenuClick: MenuProps["onClick"] = async ({ key }) => {
    if (key === "paste") {
      await handlePasteFromClipboard();
      return;
    }
    const template = eligibleTemplates.find((t) => t.key === key);
    if (template) applyConfig(template.config, template.label);
  };

  return (
    <Flex>
      <Dropdown menu={{ items: loadConfigMenuItems, onClick: handleLoadConfigMenuClick }}>
        <Button size="small" type="text" variant="solid" color="green">
          Load config <TbChevronDown size={12} />
        </Button>
      </Dropdown>
    </Flex>
  );
};
